import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { ScrollView } from "react-native";
import { useFragment } from "react-relay";

import {
  ChevronRightIcon,
  PlusIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { GiftCard } from "../GiftCard/GiftCard";
import { GiftCardListEmptyState } from "../GiftCardListEmptyState";
import { GiftCardsList_customer$key } from "./__generated__/GiftCardsList_customer.graphql";

export const GiftCardsList = ({
  customerRef,
  refetchQuery,
  openSquareModal,
  onSelectGiftCard,
  isAddingGiftCard,
  appliedGiftCardIds,
  showTotalBalance,
}: {
  customerRef: GiftCardsList_customer$key | null;
  refetchQuery: () => void;
  openSquareModal: () => void;
  onSelectGiftCard?: (giftCardId: string) => void;
  isAddingGiftCard?: boolean;
  appliedGiftCardIds?: string[];
  showTotalBalance?: boolean;
}) => {
  const customer = useFragment(GiftCardsListFragmentString, customerRef);
  const { currencySymbol } = useGetClubCurrency();

  const hasGiftCards = (customer?.allGiftCards?.edges?.length ?? 0) > 0;

  const usedGiftCards =
    customer?.allGiftCards?.edges?.filter(
      (card) => (card?.node?.balance ?? 0) <= 0
    ) ?? [];
  const availableGiftCards =
    customer?.allGiftCards?.edges?.filter(
      (card) =>
        (card?.node?.balance ?? 0) > 0 &&
        !appliedGiftCardIds?.includes(card?.node?.id ?? "")
    ) ?? [];
  const totalBalance = availableGiftCards
    .filter((card) => card?.node?.status === "ACTIVE")
    .reduce((total, card) => total + (card?.node?.balance ?? 0), 0);

  return (
    <Box flex={1} px={6} pb={6}>
      {showTotalBalance ? (
        <HStack
          pt={8}
          pb={6}
          justifyContent="space-between"
          alignItems="center"
        >
          <Text variant="mainTitle2">{t`Gift Cards`}</Text>
          <HStack gap={2} alignItems="center">
            <Text variant="mainBaseSemiBold">{t`Total balance`}</Text>
            <Text variant="mainBaseSemiBold">
              {currencySymbol}
              {ConvertCentsToDollars(totalBalance)}
            </Text>
          </HStack>
        </HStack>
      ) : null}
      <ScrollView>
        {hasGiftCards ? (
          <>
            <Box my={3} gap={3}>
              <VStack gap={2}>
                {availableGiftCards.map((card) =>
                  card?.node ? (
                    <GiftCard
                      key={card.node?.id}
                      fragmentRef={card?.node}
                      onPress={() => onSelectGiftCard?.(card?.node?.id ?? "")}
                    />
                  ) : null
                )}
              </VStack>
            </Box>
            {usedGiftCards?.length > 0 ? (
              <Box>
                <Text
                  variant="mainTiny"
                  textTransform="uppercase"
                  color="text-secondary"
                  py={2}
                >{t`Used Gift Cards`}</Text>
                <VStack gap={2}>
                  {usedGiftCards?.map((card) =>
                    card?.node ? (
                      <GiftCard key={card.node?.id} fragmentRef={card?.node} />
                    ) : null
                  )}
                </VStack>
              </Box>
            ) : null}
          </>
        ) : (
          <GiftCardListEmptyState />
        )}
        <Pressable onPress={openSquareModal}>
          <HStack gap={3} alignItems="center">
            <Box
              bg="surface-secondary"
              borderRadius="2"
              height={26}
              width={40}
              justifyContent="center"
              alignItems="center"
            >
              <PlusIcon />
            </Box>
            <Text flexGrow={1}>{t`Add New Gift Card`}</Text>
            <ChevronRightIcon />
          </HStack>
        </Pressable>
      </ScrollView>
    </Box>
  );
};

const GiftCardsListFragmentString = graphql`
  fragment GiftCardsList_customer on Customer
  @argumentDefinitions(active: { type: "Boolean", defaultValue: null }) {
    allGiftCards: squareGiftCards(active: $active) {
      edges {
        node {
          ...GiftCard_squareGiftCard
          id
          balance
          status
        }
      }
    }
  }
`;
